import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AbstractAppConfigService,
  APP_CONFIG_SERVICE,
  IHttpBasicResponse,
} from '@boxx/core';
import { Observable } from 'rxjs';
import {
  IContactApiProps,
  IContactForm,
  IContactInteractionsApiProps,
  IContactRepository,
  IGenericInteractionProps,
  IImportContactsResponse,
} from './IContact.repository';

@Injectable()
export class ContactsRepository implements IContactRepository {
  constructor(
    @Inject(APP_CONFIG_SERVICE) private appSettings: AbstractAppConfigService,
    private httpClient: HttpClient
  ) {}

  getContacts(): Observable<IHttpBasicResponse<Array<IContactApiProps>>> {
    return this.httpClient.get<IHttpBasicResponse<Array<IContactApiProps>>>(
      `${this.getBaseUrl()}`
    );
  }

  getContactInteractions(
    contactId: number
  ): Observable<IHttpBasicResponse<Array<IContactInteractionsApiProps>>> {
    return this.httpClient.get<
      IHttpBasicResponse<Array<IContactInteractionsApiProps>>
    >(`${this.getBaseUrl()}/interactions/${contactId}`);
  }

  createContact(
    payload: IContactForm
  ): Observable<IHttpBasicResponse<IContactApiProps>> {
    let params = new HttpParams();

    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        params = params.append(key, payload[key]);
      }
    }

    const body = params.toString();

    return this.httpClient.post<IHttpBasicResponse<IContactApiProps>>(
      `${this.getBaseUrl()}/create`,
      body
    );
  }

  delete(contactId: number): Observable<IHttpBasicResponse<null>> {
    return this.httpClient.delete<IHttpBasicResponse<null>>(
      `${this.getBaseUrl()}/delete/${contactId}`
    );
  }

  update(
    payload: IContactForm
  ): Observable<IHttpBasicResponse<IContactApiProps>> {
    let params = new HttpParams();

    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        params = params.append(key, payload[key]);
      }
    }

    const body = params.toString();

    return this.httpClient.post<IHttpBasicResponse<IContactApiProps>>(
      `${this.getBaseUrl()}/update/${payload.id}`,
      body
    );
  }

  import(
    payload: IContactApiProps[]
  ): Observable<IHttpBasicResponse<IImportContactsResponse>> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const data = { export_data: JSON.stringify({ contacts: payload }) };

    const urlSearchParams = new URLSearchParams();
    Object.keys(data).forEach((key: string) => {
      urlSearchParams.append(key, data[key]);
    });

    const body = urlSearchParams.toString();

    return this.httpClient.post<IHttpBasicResponse<IImportContactsResponse>>(
      `${this.getBaseUrl()}/export_from_mobile`,
      body
    );
  }

  createInteraction(
    contactId: number,
    config: IGenericInteractionProps
  ): Observable<IHttpBasicResponse<IContactInteractionsApiProps>> {
    const urlSearchParams = new URLSearchParams();
    Object.keys(config).forEach((key: string) => {
      urlSearchParams.append(key, config[key]);
    });
    const body = urlSearchParams.toString();

    return this.httpClient.post<
      IHttpBasicResponse<IContactInteractionsApiProps>
    >(`${this.getBaseUrl()}/create_interaction/${contactId}`, body);
  }

  private getBaseUrl() {
    return `${this.appSettings.baseUrl()}/api/${this.appSettings.instance()}/v1/contacts`;
  }
}

/*export const countryCallingCodes:{[key: string]: string} = {
    //Country codes, source: https://countrycode.org

    //Note:
    //https://faq.whatsapp.com/sv/wp/21016748/?category=5245236
    //Make sure to remove any leading 0's or special calling codes
    //All phone numbers in Argentina (country code "54") should have a "9"
    // between the country code and area code. The prefix "15" must be removed so the
    // final number will have 13 digits total: +54 9 xxx xxx xxxx
    //Phone numbers in Mexico (country code "52") need to have "1" after "+52", even if they're Nextel numbers.

    'ARG': '54 9',//Argentina
    'MEX': '52 1',//México
  };*/

export const COUNTRY_CALLING_CODES = [
  {
    alpha3Code: 'ARG',
    callingCodes: ['54'],
    flag: 'https://www.countryflags.io/ar/flat/64.png',
    name: 'Argentina',
    translations: { es: 'Argentina' },
  },
  {
    alpha3Code: 'MEX',
    callingCodes: ['52'],
    flag: 'https://www.countryflags.io/mx/flat/64.png',
    name: 'México',
    translations: { es: 'México' },
  },
  {
    alpha3Code: 'USA',
    callingCodes: ['1'],
    flag: 'https://www.countryflags.io/us/flat/64.png',
    name: 'Estado Unidos',
    translations: { es: 'Estado Unidos' },
  },
];
